import { extractHashtags, formatHashtag, HASHTAG_BODY_PATTERN, normalizeHashtagBody } from './hashtags'

/**
 * A single hashtag token (`#` + tag body, plus the closing `#` of a
 * multi-word tag). Built from the shared {@link HASHTAG_BODY_PATTERN} so
 * rename/add/remove accept the exact same shapes as extraction, including
 * non-ASCII tags (`#café`, `#日本語`) and multi-word ones (`#my project#`).
 * Group 1 is the raw body.
 */
const HASHTAG_TOKEN_REGEX = new RegExp(`#${HASHTAG_BODY_PATTERN}`, 'gu')

/** A hashtag token plus one optional surrounding space on each side. */
const HASHTAG_REMOVAL_REGEX = new RegExp(
  `([ \\t]?)#${HASHTAG_BODY_PATTERN}([ \\t]?)`,
  'gu',
)

/**
 * Hashtag content rewriting — pure functions shared by the web app and the
 * worker, and mirrored 1:1 in Swift (`HashtagRewriter.swift`). If you change
 * the semantics here, change them in the Swift port too.
 *
 * Handles are lowercase full hierarchical paths WITHOUT the leading `#`,
 * e.g. `work` or `work/project`.
 */

/**
 * Character ranges that must never be rewritten: fenced code blocks
 * (```...```), inline code (`...`), and markdown link/image destinations
 * (the `(...)` part of `[label](dest)` — the label itself IS rewritable).
 */
function protectedRanges(content: string): Array<[number, number]> {
  const ranges: Array<[number, number]> = []
  const codeRegex = /```[\s\S]*?```|`[^`]*`/g
  let match: RegExpExecArray | null
  while ((match = codeRegex.exec(content)) !== null) {
    ranges.push([match.index, match.index + match[0].length])
  }
  const linkRegex = /(!?\[[^\]]*\])\(([^)]*)\)/g
  while ((match = linkRegex.exec(content)) !== null) {
    const destStart = match.index + match[1].length
    ranges.push([destStart, match.index + match[0].length])
  }
  return ranges
}

/**
 * Rewrite every occurrence of `#oldHandle` (and all descendants,
 * `#oldHandle/...`) to `#newHandle` in markdown content. Used by tag rename
 * to keep note content in sync with the tag tree.
 *
 * - Matching is case-insensitive; the replacement is the lowercase new handle.
 * - `#workout` / `#work-out` are NOT matches for `work` — the full run of tag
 *   characters must equal the handle or continue with `/`.
 * - Hashtags inside code blocks, inline code, or link destinations are left
 *   untouched.
 */
export function renameHashtagInContent(
  content: string,
  oldHandle: string,
  newHandle: string,
): string {
  const oldPath = oldHandle.toLowerCase().replace(/^#/, '')
  const newPath = newHandle.toLowerCase().replace(/^#/, '')
  if (!oldPath || !newPath || oldPath === newPath) return content

  const ranges = protectedRanges(content)
  return content.replace(HASHTAG_TOKEN_REGEX, (match, name: string, offset: number) => {
    if (ranges.some(([start, end]) => offset >= start && offset < end)) return match
    // A token the user closed (`#work#`, `#my tag#`) stays closed after the
    // rename, so `#a b#c` → `#x#c` and never `#xc`.
    const emit = (handle: string) =>
      match.endsWith('#') ? `#${handle}#` : formatHashtag(handle)
    // Raw prefix first so a descendant remainder (case, trailing slash) is kept
    // verbatim; then the normalized body, which also catches `#My   Tag#`.
    const lower = name.toLowerCase()
    if (lower.startsWith(`${oldPath}/`)) {
      return emit(`${newPath}${name.slice(oldPath.length)}`)
    }
    const normalized = normalizeHashtagBody(name)
    if (normalized === oldPath) return emit(newPath)
    if (normalized.startsWith(`${oldPath}/`)) {
      return emit(`${newPath}${normalized.slice(oldPath.length)}`)
    }
    return match
  })
}

/** A line consisting solely of hashtags separated by whitespace. */
const TAG_LINE_REGEX = new RegExp(
  `^\\s*#${HASHTAG_BODY_PATTERN}(\\s+#${HASHTAG_BODY_PATTERN})*\\s*$`,
  'u',
)

/**
 * Insert a hashtag into markdown content "the smart way":
 *
 * 1. If the content already carries the tag, return it unchanged.
 * 2. If the note has a dedicated tag line (a line outside code blocks made up
 *    solely of hashtags), append the tag to the LAST such line.
 * 3. Otherwise append the tag at the end of the note, separated by a blank
 *    line.
 */
export function addHashtagToContent(content: string, handle: string): string {
  const tag = handle.toLowerCase().replace(/^#/, '')
  if (!tag) return content
  if (extractHashtags(content).includes(tag)) return content
  const token = formatHashtag(tag)
  if (!content.trim()) return token

  const lines = content.split('\n')
  let inFence = false
  let tagLineIndex = -1
  for (let i = 0; i < lines.length; i++) {
    if (/^```/.test(lines[i].trim())) {
      inFence = !inFence
      continue
    }
    if (inFence) continue
    if (lines[i].trim() && TAG_LINE_REGEX.test(lines[i])) tagLineIndex = i
  }

  if (tagLineIndex >= 0) {
    lines[tagLineIndex] = `${lines[tagLineIndex].replace(/\s+$/, '')} ${token}`
    return lines.join('\n')
  }

  return `${content.replace(/\s+$/, '')}\n\n${token}`
}

/**
 * Remove a hashtag from markdown content — the inverse of
 * {@link addHashtagToContent}. Strips every literal `#handle` occurrence and
 * tidies the whitespace it leaves behind:
 *
 * - Only the EXACT handle is removed; descendants (`#handle/...`) and prefix
 *   look-alikes (`#handleish`) are left untouched. This mirrors how tags are
 *   linked from content (only literal hashtags become note_tags rows, parents
 *   are not auto-attached), so removing `#work` never silently drops
 *   `#work/project`.
 * - Matching is case-insensitive.
 * - Hashtags inside code blocks, inline code, or link destinations are left
 *   untouched.
 * - A tag line left empty by the removal is dropped; redundant blank lines and
 *   trailing whitespace are collapsed.
 */
export function removeHashtagFromContent(content: string, handle: string): string {
  const path = handle.toLowerCase().replace(/^#/, '')
  if (!path) return content
  if (!extractHashtags(content).includes(path)) return content

  const ranges = protectedRanges(content)
  // Capture one optional space on each side so a removed tag never leaves a
  // double space or a dangling separator. We drop the leading space when there
  // is one, otherwise the trailing space, collapsing the gap to nothing.
  const stripped = content.replace(
    HASHTAG_REMOVAL_REGEX,
    (match, lead: string, name: string, trail: string, offset: number) => {
      const hashOffset = offset + lead.length
      if (ranges.some(([start, end]) => hashOffset >= start && hashOffset < end)) return match
      if (normalizeHashtagBody(name) !== path) return match
      return lead ? trail : ''
    },
  )

  // Tidy the whitespace left behind WITHOUT collapsing the user's intentional
  // blank lines elsewhere. The strip never touches newlines, so line indices are
  // stable between `content` and `stripped`. A "dropped" line is one that was a
  // tag-only line and is now empty (its tag was removed). We collapse a run of
  // blank/dropped lines to a single blank line ONLY when it contains a dropped
  // line; runs of purely intentional blank lines are preserved verbatim.
  const originalLines = content.split('\n')
  const strippedLines = stripped.split('\n')
  const isDropped = (i: number) =>
    originalLines[i].trim() !== '' &&
    TAG_LINE_REGEX.test(originalLines[i]) &&
    strippedLines[i].trim() === ''
  const isBlank = (i: number) => strippedLines[i].trim() === ''

  const out: string[] = []
  let i = 0
  while (i < strippedLines.length) {
    if (isBlank(i)) {
      // Gather the maximal run of blank/dropped lines.
      let j = i
      let containsDropped = false
      while (j < strippedLines.length && isBlank(j)) {
        if (isDropped(j)) containsDropped = true
        j++
      }
      if (containsDropped) {
        out.push('') // collapse the tag-line gap to a single blank line
      } else {
        for (let k = i; k < j; k++) out.push(strippedLines[k]) // preserve intent
      }
      i = j
    } else {
      out.push(strippedLines[i])
      i++
    }
  }
  return out.join('\n').replace(/\s+$/, '')
}
